import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';
import FeaturedNews from '@components/Home/FeaturedNews';
import FeaturedLastNews from '@components/Home/FeaturedLastNews';

import { api, mimir, site, validatesResponse } from '@/services/api';
import { TimeLine } from '@/types/types';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import { EditoriaTheme, HighLightProducts, NavBarContentData, TimeLineApi } from '@/shared/types/home.types';
import { AxiosResponse } from 'axios';
import { SchemaType } from '@/components/Internals/InternalSchema';
import { LEGACY_EDITORIA_SLUGS, monolithSlugFor, hasMonolithOverride, findEditorialByExactVariantSlug } from '@/lib/editorialRoutes';
import { shouldRenderFeaturedCarousel } from '@/lib/listingLayout';
import FeaturedProducts from '@/components/Home/FeaturedProducts';
import Layout from '@/components/Layout';
import { getEditorialCategory, getSponsoredCategory } from '@/lib/sponsors';

export type Vitrine = {
  tipo: string;
  titulo: string;
  categoria: string;
  autor: string;
  url: string;
  imagem: {
    url: string;
    blurhash: string;
  };
  data: string;
  idade: string;
};

export type HeadTags = {
  tag: string;
  content: string;
  attr: {
    rel: string;
    href: string;
    type: string | undefined;
    title: string | undefined;
    name: string | undefined;
    content: string | undefined;
    property: string | undefined;
    'http-equiv': string | undefined;
  };
};

export type FeaturedTimelineData = {
  nome: string;
  titulo: string;
  vitrine: Vitrine[];
  timeline: LastNewsItems;
  // Identidade visual data-driven (presente quando servida pelo endpoint de
  // editoria do Mimir). Alimenta o tema do FeaturedNews.
  editoria?: EditoriaTheme | null;
};

interface LastNewsItems {
  itens: TimeLine[];
  paginacao: string;
}

interface FeaturedNewsType {
  navBarData: any;
  tagsData: HeadTags[];
  segmentacoes: [];
  timelineData: FeaturedTimelineData;
  schema?: SchemaType;
  homeData: {
    data: {
      produtos: HighLightProducts[];
    };
  };
  /**
   * `true` quando a página foi servida pela editoria do Directus (slug que não é
   * categoria). O infinite scroll precisa saber disso para paginar pelo endpoint
   * de editoria — senão chamaria `categoria/<slug>` (vazio) e a p2 nunca carrega.
   */
  isEditoria?: boolean;
}

interface FeaturedNewsApi {
  data: {
    vitrine: Vitrine[];
    timeline: TimeLine[];
  };
  ssr: HeadTags[];
  segmentacoes: [];
  headers: {
    'Cache-control': string;
    Expires: string;
  };
}

// eslint-disable-next-line no-shadow
export enum PublicationTypes {
  'noticias' = '/noticias',
  'analises' = '/analises',
  'dicas' = '/dicas',
  'tutoriais' = '/tutoriais',
  'o-que-e' = '/o-que-e',
  'materias' = '/materias',
  'colunas' = '/colunas',
  'editoriais' = '/editoriais',
  'listas' = '/listas',
  'artigos' = '/artigos',
}

export default function Categories({
  homeData,
  navBarData,
  tagsData,
  segmentacoes,
  timelineData,
  schema = null,
  isEditoria = false,
}: FeaturedNewsType): JSX.Element {
  const AdsTypeGenerators = dynamic(() => import('@/components/Ads/Google/AdsTypeGenerators'), { ssr: false });

  const router = useRouter();
  const { categorias } = router.query;

  const isPublicationType = Boolean(PublicationTypes[categorias.toString()]);

  const singleCategory = Array.isArray(categorias) ? categorias[0] : categorias;

  const editorialCategory = getEditorialCategory(singleCategory);
  const sponsoredCategory = getSponsoredCategory(singleCategory);

  // Editoria de tema (ctup/eletro/ctauto) quando o slug bruto é exatamente o variant.
  const exactEditorial = typeof categorias === 'string' ? findEditorialByExactVariantSlug(categorias) : undefined;

  const showFeaturedCarousel = shouldRenderFeaturedCarousel(singleCategory);

  const getRouteType = () => {
    // Editoria do Directus → o scroll pagina pelo endpoint de editoria.
    if (isEditoria) return 'editoria';
    if (exactEditorial) return exactEditorial.routeType;
    if (categorias === 'analises') return 'tipo';
    return isPublicationType ? 'tipoDePublicacao' : 'categoria';
  };

  const getRoute = () => {
    if (isEditoria) return singleCategory;
    if (exactEditorial) return exactEditorial.homeRoute;
    if (categorias === 'analises') return 'analise';
    return categorias.toString();
  };

  return (
    <Layout layoutData={{ segmentacoes, tagsData, navBarData, schema }} dataLayer={{ contentType: 'list', category: singleCategory }}>
      {showFeaturedCarousel && (
        <FeaturedNews featuredNewsData={timelineData.vitrine} editoria={timelineData.editoria} editorialCategory={editorialCategory} />
      )}
      {categorias === 'produtos' && <FeaturedProducts featuredProductsData={homeData.data.produtos} />}
      {showFeaturedCarousel && timelineData?.vitrine?.length > 3 && (
        <AdContainer noPadding billboard>
          <AdsTypeGenerators type="billboard/main/listing" />
        </AdContainer>
      )}

      <FeaturedLastNews
        featuredLastNewsData={timelineData.timeline}
        rootPage={categorias}
        textTitle={timelineData.titulo}
        sponsoredCategory={sponsoredCategory}
        headingTag="h1"
        routeType={getRouteType()}
        route={getRoute()}
      />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  let timeline = null;
  let data = null;
  let statusCode = 200;
  let treatedResponse;

  // `context.query.categorias` vem como `string | string[] | undefined` no
  // tipo do Next; em dynamic single segment costuma ser string, mas o guard
  // mantém o SSR resiliente a entradas inesperadas (mesmo padrão de
  // `/video/[canais]/[videos].tsx`).
  const rawCategoria = Array.isArray(context.query.categorias) ? context.query.categorias[0] : context.query.categorias;
  if (!rawCategoria) return { notFound: true };

  const categorias = rawCategoria;
  const isPublicationType = Boolean(PublicationTypes[categorias as keyof typeof PublicationTypes]);

  const { host } = context.req.headers;

  // Editorias legadas (LEGACY_EDITORIA_SLUGS): a rota de categoria de mesmo slug
  // existe no monolito (devolve conteúdo, mas sem o lado Directus), então o fallback
  // de editoria mais abaixo não dispararia. Roteia direto para a rota de editoria
  // (LegacyEditoriaStrategy = merge monolito + Directus), igual aos blocos da home.
  // Slugs sem correspondência no monolito (ct-eletro → eletro) são traduzidos por
  // monolithSlugFor. Contrato completo em @/lib/editorialRoutes.
  const monolithSlug = monolithSlugFor(categorias);

  const getEndpoint = () => {
    if (LEGACY_EDITORIA_SLUGS.includes(categorias)) return `/timelines/ultimas/editoria/${categorias}/`;
    if (categorias === 'analises') return '/timelines/ultimas/tipo/analise/';
    return isPublicationType ? `/timelines/ultimas/tipo/artigo/${categorias}/` : `/timelines/ultimas/categoria/${categorias}/`;
  };

  const homeReq = site.get<FeaturedNewsApi>(`/${monolithSlug}/`);
  const timelinesReq = mimir.get<TimeLineApi>(getEndpoint());
  const responseMenuReq = api.get<NavBarContentData>(`/componentes/menu/`);
  const promises = { homeReq, timelinesReq, responseMenuReq };
  const promiseData: {
    homeReq: AxiosResponse<FeaturedNewsApi>;
    timelinesReq: AxiosResponse<TimeLineApi>;
    responseMenuReq: AxiosResponse<NavBarContentData>;
  } = {
    homeReq: null,
    timelinesReq: null,
    responseMenuReq: null,
  };

  await Promise.allSettled(Object.values(promises)).then(responses =>
    // eslint-disable-next-line consistent-return
    responses.forEach((response, index) => {
      if (response.status === 'fulfilled') {
        switch (index) {
          case 0:
            promiseData.homeReq = (response as PromiseFulfilledResult<AxiosResponse<FeaturedNewsApi>>).value;
            treatedResponse = validatesResponse(
              response.value,
              false,
              false,
              '',
              host,
              null,
              isPublicationType ? `/tipo-publicacao/${categorias}/` : `/categoria/${categorias}/`
            );
            data = treatedResponse.data;
            statusCode = treatedResponse.statusCode;
            break;
          case 1:
            promiseData.timelinesReq = (response as PromiseFulfilledResult<AxiosResponse<TimeLineApi>>).value;
            break;
          case 2:
            promiseData.responseMenuReq = (response as PromiseFulfilledResult<AxiosResponse<NavBarContentData>>).value;
            break;
          default:
            break;
        }
      }
      if (response.status === 'rejected' && index !== 0) {
        treatedResponse = validatesResponse(response.reason, true, true, categorias);
        data = treatedResponse.data;
        statusCode = treatedResponse.statusCode;
      }
    })
  );

  timeline = promiseData.timelinesReq;

  // Editorias novas (vida-digital, ct-fit) têm slug de editoria, não de
  // categoria: a timeline de categoria volta vazia e o monolito 404a. Antes de
  // desistir, tenta a timeline de editoria (mesmo shape — nome/titulo/vitrine/
  // timeline), que alimenta o page-data inline de `isLisaOnlyCategory`.
  const categoriaTimelineVazia =
    !timeline?.data?.data || ((timeline.data.data?.timeline?.itens?.length ?? 0) === 0 && (timeline.data.data?.vitrine?.length ?? 0) === 0);

  let isEditoria = false;
  if (categoriaTimelineVazia && !isPublicationType && categorias !== 'analises' && categorias !== 'ctup') {
    try {
      const editoriaRes = await mimir.get<TimeLineApi>(`/timelines/ultimas/editoria/${categorias}/`);
      const editoriaData = editoriaRes?.data?.data;
      const editoriaTemDados = (editoriaData?.timeline?.itens?.length ?? 0) > 0 || (editoriaData?.vitrine?.length ?? 0) > 0;

      if (editoriaTemDados) {
        timeline = editoriaRes;
        isEditoria = true;
      }
    } catch {
      // Editoria não resolveu — segue para o tratamento de notFound abaixo.
    }
  }

  // Slugs com override no monolito (ct-eletro → eletro) usam o endpoint de editoria
  // diretamente, mas isEditoria só é setado no fallback acima. Forçar aqui para que
  // o scroll infinito use /editoria/<slug>/ corretamente.
  if (!isEditoria && hasMonolithOverride(categorias)) {
    isEditoria = true;
  }

  if (!timeline?.data?.data) return { notFound: true };

  const timelineData = timeline.data.data;
  const timelineHasData = (timelineData?.timeline?.itens?.length ?? 0) > 0 || (timelineData?.vitrine?.length ?? 0) > 0;

  // Mimir devolve HTTP 200 com envelope `{status:3, data:{codigo:404}}` quando
  // a categoria/tipo não existe — sem esta checagem o Fawkes renderiza shell
  // vazio com HTTP 200 em vez do template 404 amigável (`pages/404.tsx`).
  const timelineEnvelopeIsNotFound = timeline.data.status === 3 && timeline.data.data?.codigo === 404;
  if (timelineEnvelopeIsNotFound && !timelineHasData) return { notFound: true };

  if (!timelineHasData) {
    if (data?.redirect) return { redirect: data.redirect };
    if (data?.notFound) return { notFound: true };
  }

  const isLisaOnlyCategory = !data || data?.notFound;
  const homeData = isLisaOnlyCategory
    ? {
        data: { vitrine: [], timeline: [] },
        ssr: [] as HeadTags[],
        segmentacoes: [],
        headers: { 'Cache-control': 'max-age=300, s-maxage=300', Expires: new Date(Date.now() + 300_000).toUTCString() },
        schema: null,
      }
    : data;

  const segmentacoes = homeData.segmentacoes || [];
  const responseMenu = promiseData.responseMenuReq;
  const navBarData = responseMenu?.data?.data ?? null;
  const schema = homeData?.schema || null;

  const categoryName = timelineData?.nome || (Array.isArray(categorias) ? categorias[0] : categorias);
  const tagsData: HeadTags[] = homeData?.ssr?.length
    ? homeData.ssr
    : ([
        { tag: 'title', content: `${categoryName} - CanalTech`, attr: {} },
        { tag: 'meta', content: '', attr: { name: 'description', content: `Últimas notícias de ${categoryName}` } },
      ] as HeadTags[]);

  if (categorias === 'ctup') {
    timelineData.titulo = 'Tudo sobre CTUP';
  }

  // `data` vindo de validatesResponse nem sempre traz `headers` (ex.: shapes de
  // redirect `{ redirect, props }` ou o early-return de resposta crua). Default
  // de cache curto garante que o setHeader não estoure quando faltam headers.
  const cacheControl = homeData.headers?.['Cache-control'] ?? 'max-age=300, s-maxage=300';
  const expires = homeData.headers?.Expires ?? new Date(Date.now() + 300_000).toUTCString();

  context.res.setHeader('Cache-Control', cacheControl);
  context.res.setHeader('Expires', expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      homeData,
      tagsData,
      navBarData,
      schema,
      timelineData,
      segmentacoes,
      isEditoria,
    },
  };
};
